import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Video, Search, Clock, CheckCircle2, AlertTriangle, Loader2,
  ChevronLeft, ChevronRight, Users, BarChart3, Eye, Shield,
  ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Briefcase, Download
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from 'recharts';
import { toast } from 'sonner';
import { getInterviewHistory } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useRegion } from '../context/RegionContext';
import { format, parseISO } from 'date-fns';

const PUBLIC_URL = typeof window !== 'undefined' ? ((process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin) : '';
const CHART_COLORS = ['#22c55e', '#eab308', '#94a3b8'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-foreground">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          {p.name}: <span className="font-semibold text-foreground">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const STATUS_CONFIG = {
  completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: Loader2 },
  pending: { label: 'Pending', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Clock },
};

const SortHeader = ({ label, field, sortField, sortDir, onSort, className = '' }) => {
  const active = sortField === field;
  return (
    <button className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors ${className}`}
      onClick={() => onSort(field)} data-testid={`sort-${field}`}>
      {label}
      {active ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
    </button>
  );
};

export default function InterviewsPage() {
  const { theme } = useTheme();
  const { region } = useRegion();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInterviews(); }, [region]);

  const loadInterviews = async () => {
    try {
      setLoading(true);
      const data = await getInterviewHistory('', 'all', 500, 0, region);
      setSessions(data.sessions || []);
    } catch (e) {
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(0);
  };

  const completed = sessions.filter(s => s.status === 'completed');
  const inProgress = sessions.filter(s => s.status === 'in_progress');
  const pending = sessions.filter(s => s.status === 'pending');

  const statusData = [
    { name: 'Completed', value: completed.length },
    { name: 'In Progress', value: inProgress.length },
    { name: 'Pending', value: pending.length },
  ].filter(d => d.value > 0);

  const scoreBuckets = [
    { range: '0-40', count: 0 }, { range: '41-60', count: 0 },
    { range: '61-80', count: 0 }, { range: '81-100', count: 0 }
  ];
  completed.forEach(s => {
    const sc = s.summary_scores?.overall || 0;
    if (sc <= 40) scoreBuckets[0].count++;
    else if (sc <= 60) scoreBuckets[1].count++;
    else if (sc <= 80) scoreBuckets[2].count++;
    else scoreBuckets[3].count++;
  });

  const jobTitles = useMemo(() => [...new Set(sessions.map(s => s.job_title).filter(Boolean))].sort(), [sessions]);

  const filtered = useMemo(() => {
    let list = sessions.filter(s => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || (s.candidate_name || '').toLowerCase().includes(q) || (s.candidate_email || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || s.status === statusFilter;
      const matchJob = jobFilter === 'all' || s.job_title === jobFilter;
      return matchSearch && matchStatus && matchJob;
    });

    list.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'candidate_name': va = (a.candidate_name || '').toLowerCase(); vb = (b.candidate_name || '').toLowerCase(); break;
        case 'job_title': va = (a.job_title || '').toLowerCase(); vb = (b.job_title || '').toLowerCase(); break;
        case 'score': va = a.summary_scores?.overall || 0; vb = b.summary_scores?.overall || 0; break;
        case 'integrity': va = a.integrity_score?.score ?? 999; vb = b.integrity_score?.score ?? 999; break;
        case 'status': va = a.status; vb = b.status; break;
        case 'created_at':
        default: va = a.created_at || a.started_at || ''; vb = b.created_at || b.started_at || ''; break;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [sessions, searchQuery, statusFilter, jobFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const gridColor = theme === 'dark' ? '#1e293b' : '#e2e8f0';
  const axisColor = theme === 'dark' ? '#64748b' : '#94a3b8';

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(parseISO(dateStr), 'MMM d, yyyy HH:mm'); } catch { return dateStr.substring(0, 16); }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="interviews-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-6">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="interviews-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          AI Interviews
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {sessions.length} interviews across {jobTitles.length} jobs
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card data-testid="interviews-stat-completed" className="cursor-pointer hover:border-emerald-500/30 transition-colors" onClick={() => { setStatusFilter('completed'); setPage(0); }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold mt-1 text-emerald-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{completed.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="interviews-stat-inprogress" className="cursor-pointer hover:border-amber-500/30 transition-colors" onClick={() => { setStatusFilter('in_progress'); setPage(0); }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                <p className="text-3xl font-bold mt-1 text-amber-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{inProgress.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="interviews-stat-pending" className="cursor-pointer hover:border-slate-500/30 transition-colors" onClick={() => { setStatusFilter('pending'); setPage(0); }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{pending.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-slate-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-slate-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="interview-score-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BarChart3 className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
              Interview Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {completed.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={scoreBuckets} barSize={40}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis dataKey="range" tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Interviews" radius={[6, 6, 0, 0]} fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">No completed interviews yet</div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="interview-status-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Video className="h-4 w-4 text-cyan-500" strokeWidth={1.8} />
              Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="45%" height={200}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={45} paddingAngle={3}>
                      {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {statusData.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i] }} />
                        <span className="text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">No interviews yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search name or email..." value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            className="pl-9 h-9" data-testid="interview-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-9" data-testid="interview-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        {jobTitles.length > 1 && (
          <Select value={jobFilter} onValueChange={v => { setJobFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[200px] h-9" data-testid="interview-job-filter"><SelectValue placeholder="All Jobs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              {jobTitles.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Badge variant="outline" className="text-xs px-2.5 py-1 h-9 flex items-center">{filtered.length} of {sessions.length}</Badge>
      </div>

      {/* Interviews Table */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-lg font-medium">No interviews found</p>
            <p className="text-sm text-muted-foreground mt-1">Generate interview links from a job's AI Interview tab</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden">
            <ScrollArea className="w-full">
              <table className="w-full text-sm" data-testid="interviews-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2.5 w-[220px]">
                      <SortHeader label="Candidate" field="candidate_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="text-left px-3 py-2.5">
                      <SortHeader label="Job" field="job_title" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="text-center px-3 py-2.5 w-[100px]">
                      <SortHeader label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-center" />
                    </th>
                    <th className="text-left px-3 py-2.5 w-[140px]">
                      <SortHeader label="Date" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="text-center px-3 py-2.5 w-[70px]">
                      <SortHeader label="Score" field="score" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-center" />
                    </th>
                    <th className="text-center px-3 py-2.5 w-[80px]">
                      <SortHeader label="Integrity" field="integrity" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-center" />
                    </th>
                    <th className="text-center px-3 py-2.5 w-[120px]">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Flags</span>
                    </th>
                    <th className="text-center px-3 py-2.5 w-[80px]">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((session) => {
                    const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.pending;
                    const StatusIcon = cfg.icon;
                    const score = session.summary_scores?.overall;
                    const intScore = session.integrity_score?.score;
                    const intLabel = session.integrity_score?.label;

                    return (
                      <tr key={session.token} className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        data-testid={`interview-row-${session.token}`}>
                        {/* Candidate */}
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="h-8 w-8 rounded-full bg-cyan-500/10 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-cyan-500">{(session.candidate_name || '?')[0].toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{session.candidate_name || 'Unknown'}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{session.candidate_email || ''}</p>
                            </div>
                          </div>
                        </td>

                        {/* Job */}
                        <td className="px-3 py-2.5">
                          <span className="text-xs text-muted-foreground truncate block max-w-[200px]">{session.job_title}</span>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-2.5 text-center">
                          <Badge className={`text-[10px] gap-0.5 ${cfg.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {cfg.label}
                          </Badge>
                        </td>

                        {/* Date */}
                        <td className="px-3 py-2.5">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(session.completed_at || session.started_at || session.created_at)}
                          </span>
                        </td>

                        {/* Score */}
                        <td className="px-3 py-2.5 text-center">
                          {score != null ? (
                            <span className={`text-sm font-bold ${score >= 70 ? 'text-emerald-500' : score >= 50 ? 'text-amber-500' : 'text-red-500'}`}
                              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {Math.round(score)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Integrity */}
                        <td className="px-3 py-2.5 text-center">
                          {intScore != null ? (
                            <div data-testid={`integrity-${session.token}`}>
                              <span className={`text-sm font-bold ${intScore >= 80 ? 'text-emerald-500' : intScore >= 50 ? 'text-amber-500' : 'text-red-500'}`}
                                style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                {intScore}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Flags */}
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1 flex-wrap">
                            {session.proctoring_violations > 0 && (
                              <Badge variant="destructive" className="text-[10px] gap-0.5 h-5" data-testid={`violations-badge-${session.token}`}>
                                <AlertTriangle className="h-2.5 w-2.5" /> {session.proctoring_violations}
                              </Badge>
                            )}
                            {session.ai_detection_summary?.max_score >= 60 && (
                              <Badge className="bg-rose-500 text-white text-[10px] gap-0.5 h-5" data-testid={`ai-risk-badge-${session.token}`}>
                                <Shield className="h-2.5 w-2.5" /> AI Risk
                              </Badge>
                            )}
                            {session.ai_detection_summary?.max_score >= 30 && session.ai_detection_summary?.max_score < 60 && (
                              <Badge className="bg-amber-500 text-white text-[10px] gap-0.5 h-5" data-testid={`ai-caution-badge-${session.token}`}>
                                <Shield className="h-2.5 w-2.5" /> AI Caution
                              </Badge>
                            )}
                          </div>
                        </td>

                        {/* Action */}
                        <td className="px-3 py-2.5 text-center">
                          {session.status === 'completed' ? (
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                                onClick={() => window.open(`${PUBLIC_URL}/interview/${session.token}?recruiter=1`, '_blank')}
                                data-testid={`view-results-${session.token}`}>
                                <Eye className="h-3 w-3" /> Results
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/interview/${session.token}/report-pdf`, '_blank')}
                                data-testid={`download-report-${session.token}`}
                                title="Download PDF Report">
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground"
                              onClick={() => {
                                navigator.clipboard.writeText(`${PUBLIC_URL}/interview/${session.token}`);
                                toast.success('Interview link copied');
                              }}
                              data-testid={`copy-link-${session.token}`}>
                              <ExternalLink className="h-3 w-3" /> Copy Link
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === 0}
                  onClick={() => setPage(p => p - 1)} data-testid="interviews-prev-page">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = totalPages <= 5 ? i : Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                  return (
                    <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="icon"
                      className="h-8 w-8 text-xs" onClick={() => setPage(pageNum)}>
                      {pageNum + 1}
                    </Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)} data-testid="interviews-next-page">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
